import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { GreenSwitch } from "./ui/green-toggle";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { toast } from "sonner";
import { stickerPackagesApi } from "../utils/api";

type StatusFilter = "all" | "active" | "inactive";

interface StickerPackage {
  _id: string;
  id?: string;
  name: string;
  badge?: string;
  badge_type?: string;
  highlight?: string;
  description?: string;
  items: string[];
  price: number;
  mrp: number;
  discount_percent: number;
  currency: string;
  is_active: boolean;
  sort_order: number;
  createdAt?: string;
  updatedAt?: string;
}

interface PackageFormState {
  name: string;
  badge: string;
  badge_type: string;
  highlight: string;
  description: string;
  itemsText: string;
  price: number;
  mrp: number;
  discount_percent: number;
  currency: string;
  is_active: boolean;
  sort_order: number;
}

const DEFAULT_FORM: PackageFormState = {
  name: "",
  badge: "",
  badge_type: "",
  highlight: "",
  description: "",
  itemsText: "",
  price: 0,
  mrp: 0,
  discount_percent: 0,
  currency: "INR",
  is_active: true,
  sort_order: 1,
};

const parseItems = (itemsText: string): string[] =>
  itemsText
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

const toFormState = (pkg: StickerPackage): PackageFormState => ({
  name: pkg.name ?? "",
  badge: pkg.badge ?? "",
  badge_type: pkg.badge_type ?? "",
  highlight: pkg.highlight ?? "",
  description: pkg.description ?? "",
  itemsText: (pkg.items ?? []).join("\n"),
  price: pkg.price ?? 0,
  mrp: pkg.mrp ?? 0,
  discount_percent: pkg.discount_percent ?? 0,
  currency: pkg.currency ?? "INR",
  is_active: pkg.is_active ?? true,
  sort_order: pkg.sort_order ?? 1,
});

const formatCurrency = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: 0,
    }).format(amount ?? 0);
  } catch {
    return `${currency || "INR"} ${amount ?? 0}`;
  }
};

export function Packages() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [packages, setPackages] = useState<StickerPackage[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingPackage, setEditingPackage] = useState<StickerPackage | null>(
    null
  );
  const [formData, setFormData] = useState<PackageFormState>(DEFAULT_FORM);
  const [actionLoadingIds, setActionLoadingIds] = useState<Set<string>>(
    new Set()
  );

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const isActive =
        statusFilter === "all" ? undefined : statusFilter === "active";
      const response = await stickerPackagesApi.getAllStickerPackages(
        currentPage,
        limit,
        searchTerm,
        isActive
      );
      const docs = response?.data?.docs ?? [];
      const pagination = response?.data ?? {};
      setPackages(docs);
      setTotalPages(pagination.totalPages ?? 0);
      setTotal(pagination.totalDocs ?? 0);
    } catch (error) {
      console.error("Error fetching sticker packages:", error);
      setPackages([]);
      setTotalPages(0);
      setTotal(0);
      toast.error("Failed to fetch packages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, [currentPage, limit, searchTerm, statusFilter]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleLimitChange = (value: string) => {
    setLimit(Number(value));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const openCreateModal = () => {
    setEditingPackage(null);
    setFormData(DEFAULT_FORM);
    setIsFormOpen(true);
  };

  const openEditModal = (pkg: StickerPackage) => {
    setEditingPackage(pkg);
    setFormData(toFormState(pkg));
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingPackage(null);
    setFormData(DEFAULT_FORM);
  };

  const buildPayload = () => {
    return {
      name: formData.name.trim(),
      badge: formData.badge.trim(),
      badge_type: formData.badge_type.trim(),
      highlight: formData.highlight.trim(),
      description: formData.description.trim(),
      items: parseItems(formData.itemsText),
      price: Number(formData.price),
      mrp: Number(formData.mrp),
      discount_percent: Number(formData.discount_percent),
      currency: formData.currency.trim().toUpperCase(),
      is_active: formData.is_active,
      sort_order: Number(formData.sort_order),
    };
  };

  const validateForm = (): boolean => {
    const payload = buildPayload();
    if (!payload.name) {
      toast.error("Package name is required");
      return false;
    }
    if (!payload.items.length) {
      toast.error("At least one item is required");
      return false;
    }
    if (!payload.currency) {
      toast.error("Currency is required");
      return false;
    }
    if (payload.price < 0 || payload.mrp < 0 || payload.discount_percent < 0) {
      toast.error("Price, MRP and discount cannot be negative");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      if (editingPackage?._id) {
        const response = await stickerPackagesApi.updateStickerPackage(
          editingPackage._id,
          payload
        );
        toast.success(response?.message || "Package updated successfully");
      } else {
        const response = await stickerPackagesApi.createStickerPackage(payload);
        toast.success(response?.message || "Package created successfully");
      }
      closeForm();
      fetchPackages();
    } catch (error) {
      console.error("Error saving package:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save package");
    } finally {
      setIsSubmitting(false);
    }
  };

  const withRowLoading = async (id: string, action: () => Promise<void>) => {
    setActionLoadingIds((prev) => new Set(prev).add(id));
    try {
      await action();
    } finally {
      setActionLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const toggleStatus = async (pkg: StickerPackage, checked: boolean) => {
    await withRowLoading(pkg._id, async () => {
      try {
        const response = await stickerPackagesApi.updateStickerPackage(pkg._id, {
          is_active: checked,
        });
        toast.success(response?.message || "Package updated successfully");
        fetchPackages();
      } catch (error) {
        console.error("Error updating package status:", error);
        toast.error("Failed to update status");
      }
    });
  };

  const handleDelete = async (pkg: StickerPackage) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete package "${pkg.name}"?`
    );
    if (!confirmed) return;

    await withRowLoading(pkg._id, async () => {
      try {
        const response = await stickerPackagesApi.deleteStickerPackage(pkg._id);
        toast.success(response?.message || "Package deleted successfully");
        fetchPackages();
      } catch (error) {
        console.error("Error deleting package:", error);
        toast.error("Failed to delete package");
      }
    });
  };

  const renderPaginationButtons = () => {
    const pages: React.ReactNode[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <Button
          key="first"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>
      );
    }

    pages.push(
      <Button
        key="prev"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
    );

    for (let i = startPage; i <= endPage; i += 1) {
      pages.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="sm"
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }

    pages.push(
      <Button
        key="next"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    );

    if (endPage < totalPages) {
      pages.push(
        <Button
          key="last"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      );
    }

    return pages;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="mb-2">Packages</h1>
          <p className="text-muted-foreground">
            Manage sticker packages with full create, edit, and delete controls
          </p>
        </div>
        <Button onClick={openCreateModal} className="w-full sm:w-auto">
          <Plus className="w-4 h-4" />
          Add Package
        </Button>
      </div>

      <Card className="shadow-sm">
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by package name..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 border border-gray-300"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                <SelectTrigger className="border border-gray-300">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>

              <Select value={String(limit)} onValueChange={handleLimitChange}>
                <SelectTrigger className="border border-gray-300">
                  <SelectValue placeholder="Limit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Package List ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">
              Loading packages...
            </div>
          ) : packages.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No packages found
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Badge</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>MRP</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Order</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {packages.map((pkg) => {
                    const rowLoading = actionLoadingIds.has(pkg._id);
                    return (
                      <TableRow key={pkg._id}>
                        <TableCell className="font-medium">{pkg.name}</TableCell>
                        <TableCell>
                          {pkg.badge ? (
                            <Badge variant="secondary">{pkg.badge}</Badge>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell className="max-w-[220px] truncate">
                          {pkg.description || "-"}
                        </TableCell>
                        <TableCell>{pkg.items?.length ?? 0}</TableCell>
                        <TableCell>{formatCurrency(pkg.price, pkg.currency)}</TableCell>
                        <TableCell>{formatCurrency(pkg.mrp, pkg.currency)}</TableCell>
                        <TableCell>{pkg.discount_percent}%</TableCell>
                        <TableCell>{pkg.sort_order}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <GreenSwitch
                              checked={pkg.is_active}
                              onChange={(checked: boolean) => toggleStatus(pkg, checked)}
                              disabled={rowLoading}
                            />
                            <Badge variant={pkg.is_active ? "default" : "secondary"}>
                              {pkg.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditModal(pkg)}
                              disabled={rowLoading}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(pkg)}
                              disabled={rowLoading}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    {renderPaginationButtons()}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={isFormOpen}
        onOpenChange={(open) => {
          if (isSubmitting) return;
          setIsFormOpen(open);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingPackage ? "Edit Package" : "Create Package"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="package-name">Name</Label>
              <Input
                id="package-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Family Pack"
                className="border border-gray-300"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="package-currency">Currency</Label>
              <Input
                id="package-currency"
                value={formData.currency}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, currency: e.target.value }))
                }
                placeholder="INR"
                className="border border-gray-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="package-badge">Badge</Label>
              <Input
                id="package-badge"
                value={formData.badge}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, badge: e.target.value }))
                }
                placeholder="POPULAR"
                className="border border-gray-300"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="package-badge-type">Badge Type</Label>
              <Input
                id="package-badge-type"
                value={formData.badge_type}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, badge_type: e.target.value }))
                }
                placeholder="pink"
                className="border border-gray-300"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="package-highlight">Highlight</Label>
              <Input
                id="package-highlight"
                value={formData.highlight}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, highlight: e.target.value }))
                }
                placeholder="Most Popular Choice"
                className="border border-gray-300"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="package-description">Description</Label>
              <Input
                id="package-description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="2 Large + 1 Small"
                className="border border-gray-300"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="package-items">Items (one item per line)</Label>
              <Textarea
                id="package-items"
                value={formData.itemsText}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, itemsText: e.target.value }))
                }
                rows={5}
                placeholder={"2 Large (3.5 inch)\n1 Small (1.5 inch)"}
                className="border border-gray-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="package-price">Price</Label>
              <Input
                id="package-price"
                type="number"
                min="0"
                value={formData.price}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    price: Number(e.target.value || 0),
                  }))
                }
                className="border border-gray-300"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="package-mrp">MRP</Label>
              <Input
                id="package-mrp"
                type="number"
                min="0"
                value={formData.mrp}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    mrp: Number(e.target.value || 0),
                  }))
                }
                className="border border-gray-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="package-discount">Discount %</Label>
              <Input
                id="package-discount"
                type="number"
                min="0"
                value={formData.discount_percent}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    discount_percent: Number(e.target.value || 0),
                  }))
                }
                className="border border-gray-300"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="package-sort-order">Sort Order</Label>
              <Input
                id="package-sort-order"
                type="number"
                min="0"
                value={formData.sort_order}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    sort_order: Number(e.target.value || 0),
                  }))
                }
                className="border border-gray-300"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="package-active">Active</Label>
              <div className="flex items-center gap-3">
                <GreenSwitch
                  checked={formData.is_active}
                  onChange={(checked: boolean) =>
                    setFormData((prev) => ({ ...prev, is_active: checked }))
                  }
                />
                <span className="text-sm text-muted-foreground">
                  {formData.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={closeForm} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting
                ? "Saving..."
                : editingPackage
                ? "Update Package"
                : "Create Package"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
